import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchSchemes, Scheme, SchemeFilters } from '../../services/scheme';

interface SchemeState {
  schemes: Scheme[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  searchQuery: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: SchemeFilters;
  hasFetched: boolean;
  error?: string;
  hasFetchedCategories: boolean;
}

const initialState: SchemeState = {
  schemes: [],
  total: 0,
  loading: false,
  page: 1,
  pageSize: 10,
  searchQuery: '',
  sortBy: '',
  sortOrder: 'asc',
  filters: { lender: '', partner: '' },
  hasFetched: false,
  hasFetchedCategories: false,
};

// --- Async thunk without generics ---
export const fetchSchemesThunk = createAsyncThunk(
  'scheme/fetchSchemes',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { scheme: SchemeState; auth: { token: string } };
      const { page, pageSize, searchQuery, sortBy, sortOrder, filters } = state.scheme;
      const { token } = state.auth;

      const response = await fetchSchemes({
        page,
        pageSize,
        sortField: sortBy,
        sortOrder,
        search: searchQuery,
        filters,
        token,
      });

      return {
        schemes: response.schemes,
        total: response.totalMatch,
        page: response.pageNo,
        pageSize: response.pageSize,
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);



const schemeSlice = createSlice({
  name: 'scheme',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    setFilters: (state, action: PayloadAction<SchemeFilters>) => {
      state.filters = action.payload;
      state.page = 1; // reset page on filter change
    },
    resetFilters: (state) => {
      state.filters = { lender: '', partner: '' };
      state.page = 1;
    },
    setHasFetched: (state, action: PayloadAction<boolean>) => {
      state.hasFetched = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchemesThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchSchemesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.schemes = action.payload.schemes;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.hasFetched = true;
      })
      .addCase(fetchSchemesThunk.rejected, (state, action) => {
        state.loading = false;
        state.schemes = [];
        state.total = 0;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setFilters,
  resetFilters,
  setHasFetched,
} = schemeSlice.actions;

export default schemeSlice.reducer;
